import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useAdmin } from "hooks/useAdmin";
import { useLogin } from "hooks/useLogin";
import { useModal } from "hooks/useModal";

import { Modal } from "components/Modal";
import { ModalRenderType } from "components/Modal/Modal.model";
import { modalIds } from "constants/modals-constants";

import { Heading3 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { Box, CssButton, CssFlex } from "styles/helpers/layout";

import type { TransferUserModalProps, TransferUsersModalParams } from "./TransferUsersModal.model";
import { StyledContainer, StyledEducationTitle, StyledSelect } from "./TransferUsersModal.styled";

export const TransferUsersModal: React.FC<TransferUserModalProps> = ({ onTransferCompleted }) => {
  const { closeModal, isModalOpen, modalParams } = useModal();
  const { t } = useTranslation("modal");

  const { getSchoolList, schoolList = [] } = useLogin();
  const { transferUsersToAnotherEducation } = useAdmin();

  const [targetSchoolId, setTargetSchoolId] = useState("");
  const [targetEducationId, setTargetEducationId] = useState("");

  const { educationId: sourceEducationId, educationTitle: sourceEducationTitle } = (modalParams ||
    {}) as unknown as TransferUsersModalParams;

  const isModalCurrentlyOpen = isModalOpen(modalIds.transferUsersToAnotherEducationModal);

  // Fetch list of schools to populate the selectors
  useEffect(() => {
    if (!schoolList.length) {
      getSchoolList();
    }
  }, [getSchoolList, schoolList]);

  // Reset selectors state when modal gets closed
  useEffect(() => {
    if (!isModalCurrentlyOpen) {
      setTargetSchoolId("");
      setTargetEducationId("");
    }
  }, [isModalCurrentlyOpen]);

  // Generate array of options for school selector
  const schoolSelectorOptions = useMemo(
    () =>
      schoolList.map((schoolItem) => ({
        value: schoolItem.id,
        label: schoolItem.title,
      })),
    [schoolList],
  );

  // Generate array of options for education selector
  const educationSelectorOptions = useMemo(() => {
    const selectedSchoolEducations =
      (targetSchoolId &&
        schoolList?.find((schoolItem) => schoolItem.id === targetSchoolId)?.educations) ||
      [];

    return selectedSchoolEducations.map((educationItem) => ({
      value: educationItem.id,
      label: educationItem.title,
      // Source education should not be selectable, so users cannot transfer to same education
      disabled: educationItem.id === sourceEducationId,
    }));
  }, [schoolList, targetSchoolId, sourceEducationId]);

  // Handler for confirm transfer button
  const onConfirmButtonClick = () => {
    transferUsersToAnotherEducation(sourceEducationId, targetEducationId).then(onTransferCompleted);
    closeModal();
  };

  return (
    <Modal
      closeOnClickOutside
      dataCy="transfer-users-to-other-education-modal"
      isOpen={isModalCurrentlyOpen}
      renderToElementType={ModalRenderType.REACT_PORTAL}
      onClose={closeModal}
    >
      <StyledContainer flexDirection="column" gap={2}>
        <Heading3>{t("transferUsersToAnotherEducationModal.title")}</Heading3>
        <ParagraphLarge>
          <Trans
            components={{
              educationTitleTag: <StyledEducationTitle data-cy="source-education-title" />,
            }}
            i18nKey="transferUsersToAnotherEducationModal.description"
            t={t}
            values={{
              sourceEducationTitle,
            }}
          />
        </ParagraphLarge>

        <CssFlex flexDirection="column" gap={1}>
          <Box>
            <label htmlFor="transfer-users-modal-school-selector">
              {t("transferUsersToAnotherEducationModal.formFields.targetSchool.label")}
            </label>
            <StyledSelect
              showSearchBox
              showPlaceholderInOptionList={false}
              aria-label={t("transferUsersToAnotherEducationModal.formFields.targetSchool.label")}
              dataCy="target-school-selector"
              id="transfer-users-modal-school-selector"
              placeholder={t(
                "transferUsersToAnotherEducationModal.formFields.targetSchool.placeholder",
              )}
              options={schoolSelectorOptions}
              value={targetSchoolId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setTargetSchoolId(e.target.value)
              }
            />
          </Box>
          <Box>
            <label htmlFor="transfer-users-modal-education-selector">
              {t("transferUsersToAnotherEducationModal.formFields.targetEducation.label")}
            </label>
            <StyledSelect
              showSearchBox
              showPlaceholderInOptionList={false}
              aria-label={t(
                "transferUsersToAnotherEducationModal.formFields.targetEducation.label",
              )}
              dataCy="target-education-selector"
              disabled={!targetSchoolId}
              id="transfer-users-modal-education-selector"
              options={educationSelectorOptions}
              placeholder={t(
                "transferUsersToAnotherEducationModal.formFields.targetEducation.placeholder",
              )}
              value={targetEducationId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setTargetEducationId(e.target.value)
              }
            />
          </Box>
        </CssFlex>

        <ParagraphLarge>
          <Trans
            components={{ b: <b /> }}
            i18nKey="transferUsersToAnotherEducationModal.warning"
            t={t}
          />
        </ParagraphLarge>

        <CssFlex gap={1} justifyContent="flex-end">
          <CssButton dataCy="cancel-transfer-button" variant="border" onClick={closeModal}>
            {t("transferUsersToAnotherEducationModal.actionButtons.cancel", "Cancel")}
          </CssButton>
          <CssButton
            dataCy="confirm-transfer-button"
            variant="primary"
            disabled={!sourceEducationId || !targetEducationId}
            onClick={onConfirmButtonClick}
          >
            {t("transferUsersToAnotherEducationModal.actionButtons.confirm", "Save")}
          </CssButton>
        </CssFlex>
      </StyledContainer>
    </Modal>
  );
};
